import api from './api';
import * as util from './util';
import { UseResource, createCachedResource, useCachedResource } from './cachedResource';

/**
 * 맞춤플랜 조회 — AP_004_002
 *
 * 한 화면에서 여러 컴포넌트가 같은 플랜을 쓴다(홈: MainCarePlan / 디지털트윈 화면 2:
 * MainCarePlan + MainCareSports). 각자 호출하면 같은 요청이 화면당 두 번 나가므로
 * 모듈 캐시로 묶어 한 번만 보낸다.
 *
 * 캐시 키가 userCusID 라서 로그아웃·계정 전환 시 이전 계정 플랜이 재사용되지 않는다.
 * 플랜을 새로 만들었을 때(AP_004_003)는 invalidateCarePlan 을 불러야 한다 —
 * CareMenu·CareSports 의 getPlan 에서 호출한다.
 */

/** AP_004_002 result_data 중 화면에서 쓰는 필드 (나머지는 그대로 통과시킨다) */
export type CarePlanRecipe = {
  name: string;
  thumbnail: string;
  kcal?: string;
  [key: string]: any;
};

export type CarePlanExercise = {
  name?: string;
  exercise_time?: string | number;
  url?: string;
};

export type CarePlanData = {
  recipe_recommend?: CarePlanRecipe;
  /** 스트레칭 목록 */
  stretching?: CarePlanExercise[];
  /** 근력운동 목록 */
  weight?: CarePlanExercise[];
  /** 유산소 권장 시간(조깅 기준, 분) */
  exercisetime?: string | number;
  [key: string]: any;
};

export type CarePlan = {
  /** 생성된 맞춤플랜이 있는지 (result_code '0'). '100' 이면 생성 전이거나 7일이 지나 재생성 대상 */
  exists: boolean;
  /** result_data 파싱 결과. exists 여도 파싱에 실패하면 null */
  data: CarePlanData | null;
};

const EMPTY: CarePlan = { exists: false, data: null };

/** 이 플랜이 누구 것인지 — 캐시 키로도 쓴다 */
const currentUserId = () => util.strNull(window.localStorage.userCusID);

export const fetchCarePlan = async (): Promise<CarePlan> => {
  // 미로그인 계정은 조회할 플랜이 없다 — 요청을 보내지 않는다
  if (currentUserId() === '') return EMPTY;

  const res = await api.get('app/api/v1/AP_004_002');
  if (res.data?.result_code !== '0') return EMPTY;

  // result_data 가 JSON 문자열이 아니면(빈 값 등) 파싱에서 예외가 나 화면이 멈춘다
  let data: CarePlanData | null = null;
  try {
    data = JSON.parse(res.data.result_data);
  } catch (e) {
    console.log('AP_004_002 result_data 파싱 실패', e);
  }
  return { exists: true, data };
};

const carePlanResource = createCachedResource(fetchCarePlan, currentUserId);

/** 캐시를 무시하고 바로 조회 (거의 쓸 일 없음 — 훅 쪽 reload 를 먼저 고려할 것) */
export const getCarePlan = (force = false) => carePlanResource.get(force);

/** 맞춤플랜을 새로 만든 뒤(AP_004_003) 호출한다 — 다음 조회에서 새 플랜을 읽는다 */
export const invalidateCarePlan = () => carePlanResource.invalidate();

export type UseCarePlan = UseResource<CarePlan>;

export const useCarePlan = (): UseCarePlan =>
  useCachedResource(carePlanResource, '맞춤플랜을 불러오지 못했습니다.');
